import React, { useState, useEffect, useMemo } from 'react';
import { Globe, ExternalLink, Copy, Check, ChevronDown, CheckCircle, Zap, BookOpen, 
         BarChart, PieChart, Target, Users, Award, Lightbulb, Layers, MessageCircle } from 'lucide-react';
import './BrandfetchGlassmorphicDisplay.css';

/**
 * BrandfetchGlassmorphicDisplay Component
 * Displays AI-focused brand information in a modern glassmorphic UI
 * 
 * @param {Object} props - Component props
 * @param {string} props.domain - The domain to fetch data for
 * @param {Function} props.onClose - Function to call when closing the display
 * @param {Function} props.onSwitchView - Function to call when switching view mode
 */
const BrandfetchGlassmorphicDisplay = ({ domain, onClose, onSwitchView }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [brandData, setBrandData] = useState(null);
    const [aiData, setAiData] = useState(null);
    const [appIdeas, setAppIdeas] = useState([]);
    const [features, setFeatures] = useState([]);
    const [marketingTips, setMarketingTips] = useState([]);
    const [contentCategories, setContentCategories] = useState([]);
    const [brandAnalysis, setBrandAnalysis] = useState(null);
    const [dominantColors, setDominantColors] = useState(['#4CAF50', '#2196F3', '#9C27B0']);
    const [activeSection, setActiveSection] = useState('insights');
    
    // Extract social links
    const socialLinks = useMemo(() => {
        return brandData?.links?.map(link => ({
            type: link.name || 'website',
            url: link.url,
            username: link.username || null,
            followers: link.followers || null,
            verified: link.verified || false
        })) || [];
    }, [brandData]);

    // Helper function to determine text color based on background
    const getTextColor = (bgColor) => {
        if (!bgColor) return '#ffffff';
        const rgb = bgColor.match(/\d+/g);
        if (!rgb) return '#ffffff';
        const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };
    
    useEffect(() => {
        if (domain) {
            fetchAllData();
        }
    }, [domain]);
    
    const handleRefresh = () => {
        fetchAllData(true);
    };
    
    const fetchAllData = async (refresh = false) => {
        try {
            setLoading(true);
            setError(null);
            
            // Normalize domain
            const normalizedDomain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
            
            // Helper safe fetch function
            const safeFetch = async (url) => {
                try {
                    const response = await fetch(url);
                    if (response.ok) {
                        return await response.json();
                    }
                    return null;
                } catch (err) {
                    console.error(`Error fetching ${url}:`, err);
                    return null;
                }
            };
            
            // Fetch all the data in parallel for better performance
            const [brandResult, aiDataResult, appIdeasResult, featuresResult, 
                   marketingTipsResult, contentCategoriesResult, brandAnalysisResult] = await Promise.all([
                safeFetch(`/api/website-data/brandfetch/${normalizedDomain}${refresh ? '?refresh=true' : ''}`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/ai-data`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/app-ideas`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/features`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/marketing-tips`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/content-categories`),
                safeFetch(`/api/website-data/domain/${normalizedDomain}/brand-analysis`)
            ]);
            
            // Process and set the data
            if (brandResult) {
                setBrandData(brandResult);
            }
            
            if (aiDataResult) {
                const processedAiData = aiDataResult.data ? aiDataResult.data : aiDataResult;
                setAiData(processedAiData);
            }
            
            if (appIdeasResult) {
                setAppIdeas(Array.isArray(appIdeasResult) ? appIdeasResult : []);
            }
            
            if (featuresResult) {
                setFeatures(Array.isArray(featuresResult) ? featuresResult : []);
            }
            
            if (marketingTipsResult) {
                setMarketingTips(Array.isArray(marketingTipsResult) ? marketingTipsResult : []);
            }
            
            if (contentCategoriesResult) {
                setContentCategories(Array.isArray(contentCategoriesResult) ? contentCategoriesResult : []);
            }
            
            if (brandAnalysisResult) {
                setBrandAnalysis(brandAnalysisResult);
            }
            
            // Extract colors from the logo or use brand colors
            if (brandResult?.colors && brandResult.colors.length > 0) {
                const brandColors = brandResult.colors.map(color => color.hex);
                setDominantColors(brandColors.slice(0, 3));
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load brand information. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    // Format platform name for display
    const formatPlatformName = (platform) => {
        const platformMap = {
            'twitter': 'Twitter/X',
            'facebook': 'Facebook',
            'instagram': 'Instagram',
            'linkedin': 'LinkedIn',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'pinterest': 'Pinterest',
            'github': 'GitHub',
            'dribbble': 'Dribbble',
            'behance': 'Behance',
            'medium': 'Medium',
            'snapchat': 'Snapchat',
            'reddit': 'Reddit',
            'discord': 'Discord',
            'twitch': 'Twitch',
            'vimeo': 'Vimeo',
            'soundcloud': 'SoundCloud',
            'spotify': 'Spotify',
            'apple': 'Apple',
            'google': 'Google',
            'microsoft': 'Microsoft',
            'android': 'Android',
            'playstore': 'Play Store',
            'appstore': 'App Store'
        };
        
        return platformMap[platform.toLowerCase()] || 
               platform.charAt(0).toUpperCase() + platform.slice(1);
    };
    
    // Get color for social platform
    const getSocialColor = (platform) => {
        const platformColors = {
            'twitter': '#1DA1F2',
            'facebook': '#1877F2',
            'instagram': '#E1306C',
            'linkedin': '#0A66C2',
            'youtube': '#FF0000',
            'tiktok': '#000000',
            'pinterest': '#E60023',
            'github': '#181717',
            'dribbble': '#EA4C89',
            'behance': '#1769FF',
            'medium': '#000000',
            'snapchat': '#FFFC00',
            'reddit': '#FF4500',
            'discord': '#5865F2',
            'twitch': '#9146FF',
            'vimeo': '#1AB7EA',
            'soundcloud': '#FF3300',
            'spotify': '#1DB954',
            'website': '#4285F4',
            'email': '#D44638',
            'phone': '#43BE31',
            'apple': '#A2AAAD',
            'google': '#4285F4',
            'microsoft': '#00A4EF',
            'android': '#A4C639',
            'playstore': '#414141',
            'appstore': '#0D96F6'
        };
        
        return platformColors[platform.toLowerCase()] || '#4285F4';
    };
    
    // Get icon for social platform
    const getSocialIcon = (platform) => {
        // Dynamic import of the required icon component would be ideal here
        // but for simplicity, we'll use Lucide icons
        
        switch (platform.toLowerCase()) {
            case 'twitter':
            case 'x.com':
                return <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                </svg>;
            default:
                return <Globe size={18} />;
        }
    };
    
    const formatFollowers = (count) => {
        if (!count || isNaN(parseInt(count))) return '';
        
        const num = parseInt(count);
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M followers`;
        } else if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K followers`;
        } else {
            return `${num} followers`;
        }
    };
    
    // Get gradient style
    const gradientStyle = useMemo(() => {
        return dominantColors.length > 0 ? {
            background: `linear-gradient(135deg, ${dominantColors.join(', ')})`,
            color: getTextColor(dominantColors[0])
        } : {};
    }, [dominantColors]);
    
    // Loading state
    if (loading) {
        return (
            <div className="glass-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="glass-container loading-state" onClick={(e) => e.stopPropagation()}>
                    <div className="glass-loading-spinner"></div>
                    <p>Analyzing brand data...</p>
                </div>
            </div>
        );
    }
    
    // Error state
    if (error || (!brandData && !aiData)) {
        return (
            <div className="glass-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="glass-container error-state" onClick={(e) => e.stopPropagation()}>
                    <div className="error-icon">⚠️</div>
                    <h2>No Information Available</h2>
                    <p>We couldn't analyze {domain} at this time.</p>
                    <button className="glass-button" onClick={handleRefresh}>Try Again</button>
                </div>
            </div>
        );
    }
    
    // Extract insights and recommendations from AI data
    const { insights = {}, recommendations = [] } = aiData || {};
    
    return (
        <div className="glass-modal" onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
        }}>
            <div className="glass-container" onClick={(e) => e.stopPropagation()}>
                {/* Header section with brand info and gradient background */}
                <div className="glass-header" style={gradientStyle}>
                    <div className="brand-logo-wrapper">
                        {brandData?.logos && brandData.logos.length > 0 && brandData.logos[0].formats ? (
                            <img 
                                src={brandData.logos[0].formats.find(format => format.src)?.src} 
                                alt={`${brandData.name || domain} logo`}
                                className="brand-logo"
                            />
                        ) : (
                            <div className="brand-logo-placeholder">
                                <Globe size={40} />
                            </div>
                        )}
                    </div>
                    
                    <div className="brand-title">
                        <h1>{brandData?.name || aiData?.company_name || domain}</h1>
                        <div className="brand-domain">
                            <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">
                                {domain} <ExternalLink size={16} />
                            </a>
                        </div>
                    </div>
                    
                    {/* View switcher */}
                    {onSwitchView && (
                        <button 
                            className="view-switcher" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onSwitchView();
                            }}
                        >
                            <span>Switch View</span>
                            <ChevronDown size={16} />
                        </button>
                    )}
                    
                    {/* Close button */}
                    <button 
                        className="close-button" 
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onClose) onClose();
                        }}
                    >
                        ×
                    </button>
                </div>
                
                {/* Navigation tabs */}
                <div className="glass-nav">
                    <button 
                        className={`nav-button ${activeSection === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveSection('insights')}
                    >
                        <Lightbulb size={18} />
                        <span>Insights</span>
                    </button>
                    <button 
                        className={`nav-button ${activeSection === 'analysis' ? 'active' : ''}`}
                        onClick={() => setActiveSection('analysis')}
                    >
                        <BarChart size={18} />
                        <span>Analysis</span>
                    </button>
                    <button 
                        className={`nav-button ${activeSection === 'social' ? 'active' : ''}`}
                        onClick={() => setActiveSection('social')}
                    >
                        <MessageCircle size={18} />
                        <span>Social</span>
                    </button>
                    <button 
                        className={`nav-button ${activeSection === 'opportunities' ? 'active' : ''}`}
                        onClick={() => setActiveSection('opportunities')}
                    >
                        <Zap size={18} />
                        <span>Opportunities</span>
                    </button>
                </div>
                
                {/* Main content area */}
                <div className="glass-content">
                    {/* Insights Section */}
                    {activeSection === 'insights' && (
                        <div className="content-section">
                            <h2>AI-Generated Insights</h2>
                            
                            {Object.keys(insights).length > 0 ? (
                                <div className="insights-grid">
                                    {Object.entries(insights).map(([key, value]) => (
                                        <div key={key} className="insight-card">
                                            <h3>{key.replace(/_/g, ' ').toUpperCase()}</h3>
                                            <p>{value}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p>No insights available for this brand yet.</p>
                                </div>
                            )}
                            
                            {recommendations && recommendations.length > 0 && (
                                <div className="recommendations-section">
                                    <h2>Strategic Recommendations</h2>
                                    <div className="recommendations-list">
                                        {recommendations.map((rec, index) => (
                                            <div key={index} className="recommendation-item">
                                                <div className="recommendation-icon">
                                                    <Target size={20} />
                                                </div>
                                                <p>{rec}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Analysis Section */}
                    {activeSection === 'analysis' && (
                        <div className="content-section">
                            <h2>Brand Analysis</h2>
                            
                            {brandAnalysis && typeof brandAnalysis === 'object' ? (
                                <div className="analysis-grid">
                                    {Object.entries(brandAnalysis).map(([key, value]) => (
                                        <div key={key} className="analysis-card">
                                            <h3>{key.replace(/_/g, ' ').toUpperCase()}</h3>
                                            <p>{typeof value === 'object' ? JSON.stringify(value) : value}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p>No analysis available for this brand yet.</p>
                                </div>
                            )}
                            
                            {contentCategories && contentCategories.length > 0 && (
                                <div className="content-categories-section">
                                    <h2>Content Categories</h2>
                                    <div className="categories-grid">
                                        {contentCategories.map((category, index) => (
                                            <div key={index} className="category-card">
                                                <h3>{category.name || `Category ${index + 1}`}</h3>
                                                <p>{category.description || 'No description available'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Social Section */}
                    {activeSection === 'social' && (
                        <div className="content-section">
                            <h2>Social Media Presence</h2>
                            
                            {socialLinks && socialLinks.length > 0 ? (
                                <div className="social-grid">
                                    {socialLinks.map((link, index) => {
                                        const platformType = link.type.toLowerCase();
                                        const platformName = formatPlatformName(link.type);
                                        const platformColor = getSocialColor(platformType);
                                        const username = link.username || link.url.split('/').filter(Boolean).pop();
                                        
                                        return (
                                            <a 
                                                key={index}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="social-card"
                                                style={{
                                                    borderLeft: `4px solid ${platformColor}`
                                                }}
                                            >
                                                <div 
                                                    className="social-icon"
                                                    style={{ 
                                                        backgroundColor: platformColor,
                                                        color: '#ffffff'
                                                    }}
                                                >
                                                    {getSocialIcon(platformType)}
                                                </div>
                                                
                                                <div className="social-details">
                                                    <div className="social-platform">
                                                        <span>{platformName}</span>
                                                        {link.verified && (
                                                            <span className="verified-badge">
                                                                <CheckCircle size={12} />
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {username && (
                                                        <span className="social-username">@{username}</span>
                                                    )}
                                                    
                                                    {link.followers && (
                                                        <span className="social-followers">
                                                            {formatFollowers(link.followers)}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                <div className="social-link-icon">
                                                    <ExternalLink size={16} />
                                                </div>
                                            </a>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p>No social links available for this brand.</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Opportunities Section */}
                    {activeSection === 'opportunities' && (
                        <div className="content-section">
                            <h2>Growth Opportunities</h2>
                            
                            {/* App Ideas */}
                            {appIdeas && appIdeas.length > 0 && (
                                <div className="opportunities-section">
                                    <h3>App Ideas</h3>
                                    <div className="ideas-grid">
                                        {appIdeas.map((idea, index) => (
                                            <div key={index} className="idea-card">
                                                <h4>{idea.title || `App Idea ${index + 1}`}</h4>
                                                <p>{idea.description || 'No description available'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Features */}
                            {features && features.length > 0 && (
                                <div className="opportunities-section">
                                    <h3>Recommended Features</h3>
                                    <div className="features-grid">
                                        {features.map((feature, index) => (
                                            <div key={index} className="feature-card">
                                                <div className="feature-icon">
                                                    <Layers size={20} />
                                                </div>
                                                <div className="feature-content">
                                                    <h4>{feature.name || `Feature ${index + 1}`}</h4>
                                                    <p>{feature.description || 'No description available'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Marketing Tips */}
                            {marketingTips && marketingTips.length > 0 && (
                                <div className="opportunities-section">
                                    <h3>Marketing Tips</h3>
                                    <div className="tips-grid">
                                        {marketingTips.map((tip, index) => (
                                            <div key={index} className="tip-card">
                                                <div className="tip-category">
                                                    {tip.category || 'Marketing Tip'}
                                                </div>
                                                <p>{tip.tip || tip.description || 'No description available'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Empty state */}
                            {(!appIdeas || appIdeas.length === 0) && 
                             (!features || features.length === 0) && 
                             (!marketingTips || marketingTips.length === 0) && (
                                <div className="empty-state">
                                    <p>No opportunities available for this brand yet.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div className="glass-footer">
                    <div className="footer-brand">
                        <span>Powered by AI</span>
                    </div>
                    <button 
                        className="refresh-button"
                        onClick={handleRefresh}
                    >
                        Refresh Data
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BrandfetchGlassmorphicDisplay; 